import { useCallback } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { SMSDataSharedTypes, SMSDataSharedUtils, SMSDataV4 } from '@frontend/api-sms-data';
import { getUser } from '@frontend/auth-helpers';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useMiniChatShallowStore } from '@frontend/mini-chat-store';
import { useAppScopeStore, useLocationGroupStore } from '@frontend/scope';
import { ThreadSendingAreaStores } from '@frontend/thread-sending-area';
import { sentry } from '@frontend/tracking';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

/**
 * A hook that returns a function to execute when a SMSNotification event is received.
 * This doesn't have any WebSocket subscription logic, just the handler. Useful for testing.
 */
export const useSMSDataV3WSHandler = () => {
  const user = getUser();

  const { selectedLocationIds } = useAppScopeStore();
  const accessibleLocationIds = useLocationGroupStore.getState().accessibleLocationIds;

  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const { setThreadTypingStatus } = ThreadSendingAreaStores.useTypingIndicatorStore();
  const { isSmsMessageItem } = SMSDataSharedUtils.threadItemsUtils;
  const { resetThreadUnreadCount } = useMiniChatShallowStore('resetThreadUnreadCount');

  const v4ThreadCountUpdater = useCallback(
    async ({
      threadIds,
      locationId,
      newStatuses,
    }: {
      threadIds: string[];
      locationId: string;
      newStatuses: SMSDataSharedTypes.CountThreadsStatusUpdate[];
    }) => {
      newStatuses.forEach((newStatus) => {
        unifiedThreadUpdater.updateThreadCounts({
          threadIds,
          locationId,
          newStatus,
          isOptimistic: false,
        });
      });
    },
    [unifiedThreadUpdater]
  );

  const wsEventHandler = useCallback<GetWebsocketEventHandler<'SMSNotification'>>(
    async (event) => {
      const { params } = event;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!params.weave_number && !checkUserLocationPhoneAccess(params.weave_number)) {
        return;
      }

      if (!!params.location_id && !accessibleLocationIds.includes(params.location_id)) {
        sentry.error({
          error: 'location ids not matching',
          topic: 'notifications',
          team: 'fe-messaging',
          severityLevel: 'error',
          addContext: {
            name: 'location_mismatch',
            context: {
              event,
              selectedLocationIds,
              accessibleLocationIds,
            },
          },
        });
      }
      switch (params.action) {
        case 'archived':
        case 'unarchived':
          if (params.thread_ids?.length && !!params.location_id) {
            const locationId = params.location_id;
            params.thread_ids.forEach(async (threadId) => {
              await unifiedThreadUpdater.upsertUnifiedThread({
                matchValues: {
                  threadId,
                  locationId,
                },
                updater: (thread) =>
                  thread
                    ? {
                        ...thread,
                        isArchived: params.action === 'archived',
                      }
                    : undefined,
                isOptimistic: false,
              });
            });
          }
          break;
        case 'bulkMarkRead':
        case 'markedRead':
          params.thread_ids?.forEach((threadId) => {
            resetThreadUnreadCount(threadId);
            setTimeout(
              () => {
                unifiedThreadUpdater.upsertUnifiedThread({
                  matchValues: {
                    threadId: threadId,
                    locationId: params.location_id ?? '',
                  },
                  updater: (oldThreadData) => {
                    if (oldThreadData) {
                      // Determine isReplied based on the first message's direction
                      const firstItem = oldThreadData.items?.[0];
                      const firstSms = firstItem && isSmsMessageItem(firstItem) ? firstItem.smsMessage : undefined;
                      const shouldBeReplied =
                        firstSms?.direction === Direction.DIRECTION_OUTBOUND && !firstSms?.autogeneratedBy;

                      return {
                        ...oldThreadData,
                        status: ThreadReadStatus.THREAD_READ_STATUS_READ,
                        isReplied: shouldBeReplied,
                      };
                    }
                    return oldThreadData;
                  },
                  isOptimistic: false,
                });
              }, // Wait for a bit if this is marked read by the classification flow on the BE to avoid race conditions
              // with the `inbound` event
              !params.weave_user_id ? 300 : 0
            );
          });

          if (params.location_id) {
            v4ThreadCountUpdater({
              threadIds: params.thread_ids ?? [],
              locationId: params.location_id,
              newStatuses: ['read'],
            });
          }

          break;
        case 'bulkMarkUnread':
        case 'markedUnread':
          params.thread_ids?.forEach((threadId) => {
            unifiedThreadUpdater.upsertUnifiedThread({
              matchValues: {
                threadId,
                locationId: params.location_id ?? '',
              },
              updater: (thread) =>
                thread
                  ? {
                      ...thread,
                      status: ThreadReadStatus.THREAD_READ_STATUS_NEW,
                      actionable: true, // marking a thread as unread should make it actionable
                    }
                  : undefined,
              isOptimistic: false,
            });
          });

          if (params.location_id) {
            v4ThreadCountUpdater({
              threadIds: params.thread_ids ?? [],
              locationId: params.location_id,
              newStatuses: ['unread'],
            });
          }
          break;
        case 'readStatusChange':
          params.thread_ids?.forEach((threadId) => {
            if (params.status === 'read') {
              resetThreadUnreadCount(threadId);
            }
            unifiedThreadUpdater.upsertUnifiedThread({
              matchValues: {
                threadId,
                locationId: params.location_id ?? '',
              },
              updater: (thread) =>
                thread
                  ? {
                      ...thread,
                      status:
                        params.status === 'read'
                          ? ThreadReadStatus.THREAD_READ_STATUS_READ
                          : ThreadReadStatus.THREAD_READ_STATUS_NEW,
                    }
                  : undefined,
              isOptimistic: false,
            });
          });
          break;
        case 'typing':
          if (user?.userID && params.weave_user_id === user?.userID) break;
          setThreadTypingStatus({
            locationId: params.location_id,
            threadId: params.thread_id,
            userId: params.weave_user_id,
            firstName: params.first_name,
            lastName: params.last_name,
            isTyping: params.is_typing,
          });
          break;
        case 'updateActionableStatus':
          if (params.location_id && params.actionable !== undefined) {
            params.thread_ids?.forEach((threadId) => {
              unifiedThreadUpdater.upsertUnifiedThread({
                matchValues: {
                  threadId: threadId,
                  locationId: params.location_id ?? '',
                },
                updater: (thread) =>
                  thread
                    ? {
                        ...thread,
                        actionable: !!params.actionable,
                        status:
                          params.actionable === false && thread.status === ThreadReadStatus.THREAD_READ_STATUS_NEW
                            ? ThreadReadStatus.THREAD_READ_STATUS_READ
                            : thread.status,
                      }
                    : undefined,
                isOptimistic: false,
              });
            });

            const newStatus = params.actionable ? 'actionable' : 'not-actionable';
            v4ThreadCountUpdater({
              threadIds: params.thread_ids ?? [],
              locationId: params.location_id,
              newStatuses: [newStatus],
            });
          }
          break;
      }
    },
    [resetThreadUnreadCount, setThreadTypingStatus, user?.userID, v4ThreadCountUpdater, unifiedThreadUpdater]
  );

  return wsEventHandler;
};

export const useSMSDataV3WSSubscription = () => {
  const handler = useSMSDataV3WSHandler();
  return useWebsocketEventSubscription('SMSNotification', handler);
};
